/** Um bloco de itens das notas, com o cabeçalho que veio no texto (quando veio). */
export interface SecaoNotas {
  titulo: string | null;
  itens: string[];
}

/** As notas de uma versão já lidas em estrutura: manchete, seções e itens. */
export interface NotasNaVersao {
  titulo: string | null;
  secoes: SecaoNotas[];
}

/** Marcador de item no texto publicado: `•`, `-` ou `*`. */
const MARCADOR = /^[•\-*]+\s*/;

/** O que cada linha do texto é: um item da lista ou uma linha de texto solta. */
interface Linha {
  item: boolean;
  texto: string;
}

/**
 * Lê as notas publicadas (texto livre, escrito à mão no release) na estrutura que a
 * tela desenha:
 *
 * - linha **com** marcador é **item**;
 * - linha **sem** marcador é **cabeçalho de seção**;
 * - a **primeira** linha sem marcador é o **título da versão** — exceto quando ela
 *   já é seguida por um item e o texto tem outras linhas sem marcador: aí ela é
 *   irmã das outras seções, e não a manchete delas.
 *
 * Nada é descartado: cabeçalho sem item continua aparecendo, porque o texto está
 * publicado e aprovado.
 */
export function interpretarNotas(notas: string | null | undefined): NotasNaVersao {
  const linhas = separarLinhas(notas);
  if (!linhas.length) return { titulo: null, secoes: [] };

  const temTitulo = primeiraLinhaEhTitulo(linhas);
  const titulo = temTitulo ? linhas[0].texto : null;
  const secoes: SecaoNotas[] = [];

  for (const linha of linhas.slice(temTitulo ? 1 : 0)) {
    if (!linha.item) {
      secoes.push({ titulo: linha.texto, itens: [] });
      continue;
    }
    // Item antes de qualquer cabeçalho (o histórico é quase todo assim): entra numa
    // seção sem título, que a tela desenha como lista solta.
    if (!secoes.length) secoes.push({ titulo: null, itens: [] });
    secoes[secoes.length - 1].itens.push(linha.texto);
  }

  return { titulo, secoes };
}

/** Descarta linhas vazias e tira o marcador dos itens, preservando o resto do texto. */
function separarLinhas(notas: string | null | undefined): Linha[] {
  return (notas ?? '')
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => {
      const item = MARCADOR.test(linha);
      return { item, texto: item ? linha.replace(MARCADOR, '').trim() : linha };
    })
    .filter((linha) => !!linha.texto);
}

/**
 * A manchete da versão. Um texto com várias seções costuma abrir com a manchete
 * seguida do primeiro cabeçalho; quando a primeira linha já é seguida de item e
 * existem outros cabeçalhos, ela é um cabeçalho como os demais (é o caso da 1.114.1,
 * que abre com "Lista Mestra").
 */
function primeiraLinhaEhTitulo(linhas: Linha[]): boolean {
  if (linhas[0].item) return false;

  const proximaEhItem = !!linhas[1]?.item;
  const temOutroCabecalho = linhas.slice(1).some((linha) => !linha.item);
  return !(proximaEhItem && temOutroCabecalho);
}
